// Library entry point for custom questions in standalone-ui
// This file exports question types and components for consumption by studio-ui

import { CourseWare, Question } from "@vue-skuilder/courseware";
import { DataShape } from "@vue-skuilder/common";
import { ViewComponent } from "@vue-skuilder/common-ui";
import { markRaw } from "vue";

// Export example course
export { exampleCourse } from "./exampleCourse";

// Export custom question classes
export { SustainedNoteQuestion } from "./SustainedNoteQuestion";
export { SequencedNoteReadingQuestion } from "./SequencedNoteReadingQuestion";

// Import classes for analysis
import { exampleCourse } from "./exampleCourse";

// Import custom question classes
import { SustainedNoteQuestion } from "./SustainedNoteQuestion";
import { SequencedNoteReadingQuestion } from "./SequencedNoteReadingQuestion";

// Import view components
import SustainedNoteQuestionView from "./SustainedNoteQuestionView.vue";
import SequencedNoteReadingQuestionView from "./SequencedNoteReadingQuestionView.vue";

// Set up views to avoid circular dependency
// Views should be the Vue components directly - they have a .name property
SustainedNoteQuestion.views = [
  markRaw(SustainedNoteQuestionView)
];
SequencedNoteReadingQuestion.views = [
  markRaw(SequencedNoteReadingQuestionView)
];

// Import inline components for markdown
import FluteFingeringInline from "../components/FluteFingeringInline.vue";
import MusicNote from "../components/MusicNote.vue";

/**
 * Main function to export all custom questions for studio-ui consumption
 * This provides a standardized interface for the CLI to discover and integrate
 * custom question types into studio-ui builds
 */
export function allCustomQuestions() {
  // Collect all question classes
  const questionClasses: (typeof Question)[] = [
    SustainedNoteQuestion,
    SequencedNoteReadingQuestion,
  ];

  // Collect all data shapes from questions
  const dataShapes: DataShape[] = [];
  questionClasses.forEach((questionClass) => {
    if (questionClass.dataShapes) {
      questionClass.dataShapes.forEach((shape) => {
        // Avoid duplicates
        if (!dataShapes.find((existing) => existing.name === shape.name)) {
          dataShapes.push(shape);
        }
      });
    }
  });

  // Collect all view components from questions
  const views: ViewComponent[] = [];
  questionClasses.forEach((questionClass) => {
    if (questionClass.views) {
      questionClass.views.forEach((view) => {
        // Avoid duplicates by name
        if (!views.find((existing) => existing.name === view.name)) {
          views.push(view);
        }
      });
    }
  });

  const courses = [exampleCourse];

  // Inline markdown components for use in card content
  const inlineComponents = {
    fluteFingering: markRaw(FluteFingeringInline),
    musicNote: markRaw(MusicNote),
  };

  // Return structured data for studio-ui integration
  return {
    // CourseWare instances with question instances
    courses,

    // Question class constructors for registration
    questionClasses,

    // Available data shapes for studio-ui CreateCardView
    dataShapes,

    // Vue components for runtime registration
    views,

    // Inline markdown components (for {{ <component /> }} syntax)
    inlineComponents,

    // Metadata for debugging and analysis
    meta: {
      questionCount: questionClasses.length,
      dataShapeCount: dataShapes.length,
      viewCount: views.length,
      courseCount: courses.length,
      inlineComponentCount: Object.keys(inlineComponents).length,
      packageName: "@vue-skuilder/standalone-ui",
      sourceDirectory: "src/questions",
    },
  };
}

/**
 * Type definitions for the custom questions export structure
 * This provides TypeScript support for CLI and studio-ui integration
 */
export interface CustomQuestionsExport {
  courses: CourseWare[];
  questionClasses: Array<typeof Question>;
  dataShapes: DataShape[];
  views: ViewComponent[];
  inlineComponents: Record<string, any>;
  meta: {
    questionCount: number;
    dataShapeCount: number;
    viewCount: number;
    courseCount: number;
    inlineComponentCount: number;
    packageName: string;
    sourceDirectory: string;
  };
}

// Default export for convenience
export default allCustomQuestions;
